import { Controller } from "@hotwired/stimulus";

// Auto-inserts the dashes in a YYYY-MM-DD birthday field as you type digits
// -- "19830701" becomes "1983-07-01" -- so typing it as one run of digits
// still works, without requiring the dashes to be typed by hand. Purely a
// display/typing convenience: the field's name/value shape submitted to
// the server is unchanged (still one plain "trav[N][birthday]" string in
// the same YYYY-MM-DD format Step2Controller#pax_birthday already expects,
// see _booking_form.html.erb's comment on that field).
//
// Each digit is only accepted if the date can still turn out valid: the
// year must be 19xx/20xx, the month 01-12 and the day within that month
// (leap years included), so "1983-99-99" can't be typed at all. A month
// digit of 2-9 or a day digit of 4-9 (3 for February) can only be a
// single-digit value, so it's zero-padded straight away ("1983-7" ->
// "1983-07").
export default class extends Controller {
  format(event) {
    const digits = event.target.value.replace(/\D/g, "");
    let accepted = "";
    for (const digit of digits) {
      if (accepted.length >= 8) break;
      accepted = acceptDigit(accepted, digit);
    }

    let formatted = accepted.slice(0, 4);
    if (accepted.length > 4) formatted += `-${accepted.slice(4, 6)}`;
    if (accepted.length > 6) formatted += `-${accepted.slice(6, 8)}`;
    event.target.value = formatted;
  }
}

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

// Returns `accepted` with `digit` appended (zero-padded where only a
// single-digit value is possible), or `accepted` unchanged if the digit
// can't lead to a valid date.
function acceptDigit(accepted, digit) {
  const d = Number(digit);
  switch (accepted.length) {
    case 0:
      return d === 1 || d === 2 ? accepted + digit : accepted;
    case 1:
      return (accepted === "1" && d === 9) || (accepted === "2" && d === 0) ? accepted + digit : accepted;
    case 2:
    case 3:
      return accepted + digit;
    case 4:
      if (d <= 1) return accepted + digit;
      return `${accepted}0${digit}`;
    case 5: {
      const month = Number(accepted[4] + digit);
      return month >= 1 && month <= 12 ? accepted + digit : accepted;
    }
    case 6: {
      const maxDay = daysInMonth(Number(accepted.slice(0, 4)), Number(accepted.slice(4, 6)));
      return d * 10 <= maxDay ? accepted + digit : `${accepted}0${digit}`;
    }
    case 7: {
      const maxDay = daysInMonth(Number(accepted.slice(0, 4)), Number(accepted.slice(4, 6)));
      const day = Number(accepted[6] + digit);
      return day >= 1 && day <= maxDay ? accepted + digit : accepted;
    }
    default:
      return accepted;
  }
}

// True for a real calendar date in YYYY-MM-DD form -- used by
// step2_submit_controller to catch values that didn't come through
// typing (e.g. a remembered traveller from an earlier visit).
export function isValidDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [year, month, day] = match.slice(1).map(Number);
  return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month);
}
